import { Button, CreateButton, EditButton, ExportButton, Form, Icons, List, Modal, Space, Switch, Table, Typography, useDrawerForm, useEditableTable } from '@pankod/refine-antd'
import { IResourceComponentsProps, useTranslate } from '@pankod/refine-core'
import { CreateCode } from 'components/codes/create'
import { definitions } from 'interfaces'
import { automaticCouponFilters } from './visibility'
import { useState } from 'react'
import { supabaseClient } from 'utility'
import { utils, writeFile } from 'xlsx'

export const CodesList: React.FC<IResourceComponentsProps> = () => {
  const [showAutomatic, setShowAutomatic] = useState(false)
  const {
    tableProps,
    formProps,
    setId: setEditId,
    tableQueryResult
  } = useEditableTable<definitions['codes']>({
    initialSorter: [
      {
        field: 'id',
        order: 'asc'
      }
    ],
    permanentFilter: [
      {
        field: 'deleted',
        operator: 'eq',
        value: false
      },
      ...automaticCouponFilters(showAutomatic)
    ],
    metaData: {
      select: 'id, code, value, description, enabled, scope, type, apply_for, email'
    },
    hasPagination: true
  })

  const {
    drawerProps: createDrawerProps,
    formProps: createFormProps,
    show: createShow,
    close: createClose
  } = useDrawerForm<definitions['codes']>({
    action: 'create',
    resource: 'codes',
    redirect: false
  })

  const {
    drawerProps: editDrawerProps,
    formProps: editFormProps,
    show: editShow,
    close: editClose
  } = useDrawerForm<definitions['codes']>({
    action: 'edit',
    resource: 'codes',
    redirect: false
  })

  const [showModal, setShowModal] = useState<boolean>(false)
  const [currentId, setCurrentId] = useState<number>(-1)

  const deleteCode = async () => {
    if (currentId > -1) {
      await supabaseClient
        .from('codes')
        .update({
          deleted: true,
          enabled: false
        })
        .eq('id', currentId)
    }
    setShowModal(false)
    setCurrentId(-1)
    tableQueryResult.refetch()
  }

  const t = useTranslate()

  const downloadUsers = (record: any) => {
    if (record.value.conditions) {
      const { group } = record.value.conditions.find((x: any) => x.group)
      const usersList = group.map((x: any) => {
        return { users: x.email }
      })
      const worksheet = utils.json_to_sheet(usersList, { skipHeader: true })
      const workbook = utils.book_new()
      utils.book_append_sheet(workbook, worksheet, 'Sheet1')
      writeFile(workbook, `users_code_${record.code}.xlsx`)
    }
  }

  return (
    <List title={t('codes.title')} headerButtons={
      <CreateButton onClick={() => createShow()}>
        {t('codes.actions.addCode')}
      </CreateButton>
    }>
      <Space style={{ marginBottom: 16 }}>
        <Switch checked={showAutomatic} onChange={(checked) => { setShowAutomatic(checked); tableProps.onChange?.({ ...(tableProps.pagination || {}), current: 1 }, {}, {}, { action: 'paginate', currentDataSource: tableProps.dataSource ? [...tableProps.dataSource] : [] }) }} aria-label='Mostrar cupones automáticos de CRM' />
        <Typography.Text>Mostrar también cupones automáticos de CRM</Typography.Text>
      </Space>
      <Form {...formProps}>
        <Table
          {...tableProps}
          rowKey="id"
          onRow={(record) => ({
            onClick: (event: any) => {
              if (event.target.nodeName === 'TD') {
                setEditId && setEditId(record.id)
              }
            }
          })}
        >
          <Table.Column
            dataIndex="id"
            title='ID'
            align='center'
            sorter
          />
          <Table.Column
            dataIndex="code"
            title={t('codes.fields.code')}
            sorter
          />
          <Table.Column
            dataIndex="description"
            title={t('codes.fields.description')}
            sorter
          />
          <Table.Column
            dataIndex="scope"
            title={t('codes.fields.scope')}
            sorter
          />
          <Table.Column
            dataIndex="type"
            title={t('codes.fields.type')}
            sorter
          />
          <Table.Column
            dataIndex="apply_for"
            title={t('codes.fields.apply_for')}
            sorter
            render={(value) => t(`codes.fields.${value}`)}
          />
          <Table.Column
            dataIndex="email"
            title={t('codes.fields.email')}
            sorter
          />

          <Table.Column
            title={t('table.actions')}
            align='center'
            dataIndex="actions"
            render={(_, record: any) => {
              return (
                <>
                  <Space>
                    {(record.scope === 'group') && (
                      <ExportButton
                        onClick={() => downloadUsers(record)}
                        hideText
                        size='small'
                      />
                    )}
                    <EditButton
                      onClick={() => editShow(record.id)}
                      hideText
                      size='small'
                    />
                    <Button
                      onClick={() => {
                        setShowModal(true)
                        setCurrentId(record.id)
                      }}
                      icon={<Icons.DeleteOutlined style={{ color: 'red' }} />}
                      size='small'
                    />
                  </Space>
                </>
              )
            }}
          />
        </Table>
      </Form>
      <CreateCode
        drawerProps={createDrawerProps}
        formProps={createFormProps}
        tableQueryResult={tableQueryResult}
        close={createClose}
        isEdit={false}
      />
      <CreateCode
        drawerProps={editDrawerProps}
        formProps={editFormProps}
        tableQueryResult={tableQueryResult}
        close={editClose}
        isEdit={true}
      />
      <Modal visible={showModal} onCancel={() => setShowModal(false)} onOk={() => deleteCode()}>
        <Space>
          <Typography>{t('codes.modalTitle')}</Typography>
        </Space>
      </Modal>
    </List>
  )
}
